import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Phone, MessageCircle, MapPin, ChevronDown } from "lucide-react";
import { BRAND, NAV } from "@/data/brand";

export default function Footer() {
  const [openAbb, setOpenAbb] = useState(false);
  return (
    <footer data-testid="site-footer" className="relative bg-[color:var(--ink)] text-white">
      {/* Fucsia top curve */}
      <svg className="block w-full text-[color:var(--fucsia)]" viewBox="0 0 1440 80" preserveAspectRatio="none" aria-hidden="true">
        <path fill="currentColor" d="M0,60 C240,10 480,90 720,60 C960,30 1200,10 1440,50 L1440,0 L0,0 Z" />
      </svg>

      <div className="container-x py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Link to="/" className="inline-flex items-center gap-3">
              <div className="bg-white rounded-2xl px-4 py-2">
                <img src="/img/logo.webp" alt={BRAND.name} className="h-10 w-auto object-contain" />
              </div>
            </Link>
            <p className="font-display text-3xl md:text-4xl leading-[1.1] mt-8 max-w-md">
              Da oltre <em className="text-[color:var(--fucsia-2)] not-italic">30 anni</em> nel quartiere <span className="italic">La Scopaia</span>.
            </p>
            <p className="text-white/70 mt-6 max-w-md">
              Abbigliamento, lavanderia, stireria, sartoria e prodotti per il bucato. Un unico punto di riferimento a Livorno.
            </p>

            <div className="mt-8 flex items-center gap-3">
              <a data-testid="footer-instagram" href={BRAND.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-11 h-11 rounded-full border border-white/15 inline-flex items-center justify-center hover:bg-[color:var(--fucsia)] hover:border-[color:var(--fucsia)] transition-colors">
                <Instagram className="w-4 h-4" />
              </a>
              <a data-testid="footer-facebook" href={BRAND.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-11 h-11 rounded-full border border-white/15 inline-flex items-center justify-center hover:bg-[color:var(--fucsia)] hover:border-[color:var(--fucsia)] transition-colors">
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="section-label text-[color:var(--fucsia-2)]">Navigazione</div>
            <ul className="mt-6 space-y-3">
              {NAV.map((n) =>
                n.children ? (
                  <li key={n.href}>
                    <button
                      onClick={() => setOpenAbb((v) => !v)}
                      className="w-full flex items-center justify-between text-white/80 hover:text-white transition-colors"
                      aria-expanded={openAbb}
                      data-testid="footer-abbigliamento-toggle"
                    >
                      <span>{n.label}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${openAbb ? "rotate-180 text-[color:var(--fucsia-2)]" : ""}`} />
                    </button>
                    <div
                      className="overflow-hidden transition-[max-height,opacity,margin] duration-300 ease-out"
                      style={{
                        maxHeight: openAbb ? "200px" : "0px",
                        opacity: openAbb ? 1 : 0,
                        marginTop: openAbb ? "8px" : "0px",
                      }}
                    >
                      <ul className="pl-4 py-2 space-y-2 border-l border-white/15">
                        {n.children.map((c) => (
                          <li key={c.href}>
                            <Link to={c.href} className="text-sm text-white/70 hover:text-white transition-colors">
                              {c.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                ) : (
                  <li key={n.href}>
                    <Link to={n.href} className="text-white/80 hover:text-white transition-colors">
                      {n.label}
                    </Link>
                  </li>
                )
              )}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="section-label text-[color:var(--fucsia-2)]">Contatti</div>
            <ul className="mt-6 space-y-4 text-white/85">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-[color:var(--fucsia-2)]" />
                <span>
                  {BRAND.address}
                  <br />
                  {BRAND.postalCode} {BRAND.city} — La Scopaia
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[color:var(--fucsia-2)]" />
                <a href={`tel:${BRAND.phoneRaw}`} className="hover:text-white">{BRAND.phone}</a>
              </li>
              <li className="flex items-center gap-3">
                <MessageCircle className="w-4 h-4 text-[color:var(--fucsia-2)]" />
                <a href={BRAND.whatsappUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white">{BRAND.whatsapp}</a>
              </li>
            </ul>

            <div className="mt-8 rounded-2xl bg-white/[0.05] border border-white/10 p-5">
              <div className="section-label text-[color:var(--fucsia-2)]">Orari — Invernale</div>
              <div className="mt-3 text-sm text-white/85 space-y-1.5">
                {BRAND.hoursWinter.map((r, i) => (
                  <div key={i} className="flex justify-between gap-4"><span>{r.d}</span><span className="text-white/70">{r.h}</span></div>
                ))}
              </div>
            </div>

            <div className="mt-4 rounded-2xl bg-white/[0.05] border border-white/10 p-5">
              <div className="section-label text-[color:var(--fucsia-2)]">Orari — Estivo</div>
              <div className="mt-3 text-sm text-white/85 space-y-1.5">
                {BRAND.hoursSummer.map((r, i) => (
                  <div key={i} className="flex justify-between gap-4"><span>{r.d}</span><span className="text-white/70">{r.h}</span></div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-white/60">
          <div>© {new Date().getFullYear()} Abbigliamento Tatiana · P. Iva —</div>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/cookie" className="hover:text-white">Cookie Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
